import React, { useState, useEffect, useContext, useRef } from 'react';
import ModalEliminacion from '../modal/ModalEliminacion';
import { AuthContext } from '../contexts/AuthContext'; 

interface ProveedorProps 
{
  provider?: 
  { 
    id: number; 
    name: string; 
    api_key: string; 
    valid_ips: string 
  };

  onSave: (providerData: 
  { 
    id?: number;
    name: string; 
    api_key?: string; 
    valid_ips: string 
  }) => void;

  onDelete?: (providerId: number) => void;

  onCancel: () => void;
}

const Proveedor: React.FC<ProveedorProps> = ({ provider, onSave, onDelete, onCancel }) => 
{
  const [name, setName] = useState('');
  const [api_key, setApiKey] = useState('');
  const [valid_ips, setValidIps] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para el modal
  const modalRef = useRef<HTMLDivElement>(null); // Ref para el contenedor del modal

  const { token } = useContext(AuthContext);

  // Determinar si estamos en modo de edición o creación
  const isEditing = provider && provider.id !== -1;

  //-----------------------------------------------------------

  useEffect(() => 
  {
    if (provider) 
    {
      setName(provider.name);
      setApiKey(provider.api_key);
      setValidIps(provider.valid_ips);
    }
    else
    {
      setName('');
      setApiKey('');
      setValidIps('');
    }
  }, [provider]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    // Función para manejar clics fuera del componente
    const handleClickOutside = (event: MouseEvent) => 
    {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    // Función para manejar presiones de teclas
    const handleEscPress = (event: KeyboardEvent) => 
    {
      if (event.key === 'Escape') 
      {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscPress);

    return () => 
    {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [onCancel]);  

  //-----------------------------------------------------------

  const handleSubmit = async (event: React.FormEvent) => 
  {
    event.preventDefault();
    try 
    {
      const formData = new URLSearchParams();

      formData.append('name', name);
      formData.append('valid_ips', valid_ips);  
      formData.append('api_key', api_key);
      
      if (isEditing && provider)
        formData.append('id', provider.id.toString());
  
      const url = isEditing ? 'web/update_provider' : 'web/create_provider';
  
      const api_url = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(api_url + url, {
        method: 'POST',
        headers: {
          'X-AUTH-TOKEN': token || '',
          'Content-Type': 'application/x-www-form-urlencoded'},
        body: formData.toString()});
  
      if (response.ok) 
      {
        onSave({ 
          id: provider?.id ?? undefined, 
          name, 
          api_key, 
          valid_ips });
      } 
      else 
      {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar el proveedor.');
      }
    } 
    catch (error) 
    {
      setError('Error de red. Por favor, intente nuevamente.');
    }
  };

  //-----------------------------------------------------------

  const handleDelete = () => 
  {
    if (provider && provider.id !== null && provider.id !== -1) 
      {
        setIsModalOpen(true);
      }
  };

  //-----------------------------------------------------------

  const handleConfirmDelete = async () => 
  {
    if (provider && provider.id !== null && provider.id !== -1 && onDelete) 
    {
      try 
      {
        const formData = new URLSearchParams();
        formData.append('id', provider.id.toString());
  
        const api_url = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${api_url}web/delete_provider`, {
          method: 'POST',
          headers: {
            'X-AUTH-TOKEN': token || '',
            'Content-Type': 'application/x-www-form-urlencoded'},
          body: formData.toString()});
  
        if (response.ok) 
        {
          onDelete(provider.id); // Notificar a Providers.tsx que el proveedor fue eliminado
          setIsModalOpen(false); // Cerrar el modal
        } 
        else 
        {
          const errorData = await response.json();
          setError(errorData.error || 'Error al eliminar el proveedor.');
        }
      }
      catch (error) 
      {
        setError('Error de red. Por favor, intente nuevamente.');
      }
    }
  };

  //-----------------------------------------------------------

  return (
    <div className="evento-container" ref={modalRef}>
      <h2>{isEditing ? 'Editar Proveedor' : 'Crear Nuevo Proveedor'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del proveedor"
              required
            />
        </div>
        <div className="form-group">
          <input
              id="api_key"
              type="text"
              value={api_key}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="API key (se generará automáticamente si se deja vacío)"
            />
        </div>
        <div className="form-group">
          <input
              id="valid_ips"
              type="text"
              value={valid_ips}
              onChange={(e) => setValidIps(e.target.value)}
              placeholder="Todas las IPs son válidas"
            />          
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="form-actions">
          <button type="submit">{isEditing ? 'Guardar' : 'Crear'}</button>
          <button type="button" onClick={onCancel}>Cancelar</button>
          {isEditing && provider && provider.id !== null && provider.id !== -1 && (
            <div className="delete-button-container">
            <button type="button" className="delete-event-button" onClick={handleDelete}>
            <i className="fas fa-trash"></i>Borrar
            </button>
            </div>
          )}
        </div>
      </form>
      {isModalOpen && (
        <ModalEliminacion
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}>
          <p>¿Estás seguro de que quieres eliminar este proveedor?</p>
        </ModalEliminacion>
      )}
    </div>
  );
};

export default Proveedor;
